import { isEmpty } from 'rambda';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTypedParams, useTypedSearchParams } from 'react-router-typesafe-routes';
import SearchInput from '@/components/common/search-input';
import ConversationSearchResult from '@/components/conversations/conversation-search-result';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useSearchMessagesBySession from '@/hooks/conversations/use-search-messages-by-session';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { generateSessionKey } from '@/utils/session';

interface Props {
  type: ConversationType;
}

export default function SearchMessagesBySessionRoute({ type }: Props) {
  const { t } = useTranslation();
  const { id } = useTypedParams(ROUTES.MESSAGES.SESSIONS.SEARCH);
  const [searchParams, setSearchParams] = useTypedSearchParams(ROUTES.MESSAGES.SESSIONS.SEARCH);

  const sessionKey = generateSessionKey(type, id);
  const {
    form: { control, handleSubmit },
    searchKeyword,
    submit,
    clear,
    result,
    helperText,
  } = useSearchMessagesBySession(sessionKey, searchParams.keyword);

  const handleSearch: typeof submit = ({ keyword }) => {
    if (!isEmpty(keyword)) {
      setSearchParams({ keyword }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }

    return submit({ keyword });
  };

  return (
    <PageLayout
      title={t('conversation.search.title')}
      headerConfig={{
        navigateBack: true,
        elementAfterNav: (
          <form
            className='mx-4 flex items-center gap-1 xs:gap-2 desktop:mt-4 desktop:mx-0'
            onSubmit={handleSubmit(handleSearch)}>
            <Controller
              control={control}
              name='keyword'
              render={({ field: { ref, value, onChange, onBlur } }) => (
                <SearchInput
                  ref={ref}
                  keyword={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder={t('conversation.search.placeholder')}
                  clearKeyword={value ? clear : undefined}
                  className='flex-1 desktop:!m-0'
                  icon
                />
              )}
            />
          </form>
        ),
      }}>
      <main className='flex w-full flex-1 flex-col gap-2 px-5 desktop:overflow-auto'>
        <div className='desktop:px-4'>
          {helperText && <div className='w-full text-center text-secondary'>{helperText}</div>}

          <ConversationSearchResult
            result={result}
            sessionKey={sessionKey}
            keyword={searchKeyword}
          />
        </div>
      </main>
    </PageLayout>
  );
}
